import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone,
  PhoneOff,
  X,
  Mic,
  MicOff,
  Pause,
  Play,
  Minimize2,
  Maximize2,
  ShoppingCart,
  Shield,
  PhoneCall,
  Volume2,
  VolumeX,
  Grid,
  Plus,
  Disc,
  Minus,
  User,
  ChevronDown,
} from "lucide-react";
import aarogyaLogo from "../assets/Aarogya india hindia updated logo.svg";
import { leadsApi } from "../services/api";
import { useTheme } from "../context/ThemeContext";
import { BASE_URL as API_URL } from '../services/api';

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatTime = (sec) => {
  const m = Math.floor(sec / 60)
    .toString()
    .padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const maskPhone = (phone) => {
  if (!phone) return "XXXXXX0000";
  const str = String(phone).replace(/\D/g, "");
  if (str.length < 4) return phone;
  const last4 = str.slice(-4);
  return `XXXXXX${last4}`;
};

const DISPOSITIONS = [
  { key: "ORDER PLACED", label: "Order Placed", color: "#38A169" },
  { key: "CALL BACK", label: "Call Back", color: "#D69E2E" },
  { key: "NOT INTERESTED", label: "Not Interested", color: "#E53E3E" },
  { key: "NOT CONNECTED", label: "Not Connected", color: "#718096" },
  { key: "CALL DISCONNECTED", label: "Call Disconnected", color: "#DD6B20" },
  { key: "PRANK CALL", label: "Prank Call", color: "#9B2C2C" },
  { key: "SWITCH OFF", label: "Switch Off", color: "#553C9A" },
  { key: "FUTURE CALL BACK", label: "Future Call Back", color: "#2B6CB0" },
  { key: "VERIFIED", label: "Verified", color: "#276749" },
  { key: "REJECTED", label: "Rejected", color: "#C53030" },
];

// ── Spinner ───────────────────────────────────────────────────────────────────
const Spin = ({ color = "#fff", size = 14 }) => (
  <span
    style={{
      width: size,
      height: size,
      border: `2px solid ${color}`,
      borderTopColor: "transparent",
      borderRadius: "50%",
      display: "inline-block",
      animation: "dialerSpin 0.8s linear infinite",
      flexShrink: 0,
    }}
  />
);

// ── Botanical Leaves Top-Left SVG ─────────────────────────────────────────────
const BotanicalLeaves = () => (
  <svg
    width="38"
    height="38"
    viewBox="0 0 60 60"
    fill="none"
    style={{ pointerEvents: "none", display: "block" }}
  >
    <path
      d="M6 52 Q 22 32, 46 16"
      stroke="#527d45"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
    <path
      d="M46 16 C 39 8, 26 11, 29 23 C 33 35, 43 23, 46 16 Z"
      fill="#6e965f"
    />
    <path
      d="M29 28 C 20 20, 10 27, 15 35 C 21 43, 28 34, 29 28 Z"
      fill="#84ad75"
    />
    <path
      d="M19 39 C 10 36, 4 45, 10 52 C 16 59, 21 46, 19 39 Z"
      fill="#58804b"
    />
    <path
      d="M38 20 C 32 10, 20 14, 24 24 C 28 34, 37 26, 38 20 Z"
      fill="#9bc489"
      opacity="0.85"
    />
  </svg>
);

// ── Concentric Avatar (Sage Green Rings with User Icon) ─────────────────────────
const ConcentricAvatar = ({ isDialing = false, isDark = false }) => (
  <div
    style={{
      width: 104,
      height: 104,
      borderRadius: "50%",
      background: isDark ? "rgba(68, 153, 120, 0.15)" : "#edf7e7",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 12px",
      animation: isDialing ? "concentricPulse 2s infinite ease-in-out" : "none",
      transition: "all 0.3s ease",
    }}
  >
    <div
      style={{
        width: 82,
        height: 82,
        borderRadius: "50%",
        background: isDark ? "rgba(68, 153, 120, 0.28)" : "#d6ecc8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: "50%",
          background: "#3f683a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 14px rgba(63, 104, 58, 0.35)",
        }}
      >
        <User size={28} color="#ffffff" strokeWidth={2.4} />
      </div>
    </div>
  </div>
);

// ── Circular Action Button ───────────────────────────────────────────────────
const CircleActionButton = ({
  icon,
  label,
  onClick,
  active = false,
  activeBg = "#d8eccf",
  activeColor = "#2d5a27",
  loading = false,
  isDark = false,
}) => (
  <button
    onClick={onClick}
    disabled={loading}
    style={{
      background: "none",
      border: "none",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      cursor: loading ? "wait" : "pointer",
      padding: 0,
      outline: "none",
      transition: "transform 0.15s ease",
    }}
    onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.93)")}
    onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
  >
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        background: active
          ? activeBg
          : isDark
            ? "rgba(255,255,255,0.08)"
            : "#f0f5ed",
        border: active
          ? `1.5px solid ${activeColor}`
          : isDark
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.03)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: active ? activeColor : isDark ? "#e2e8f0" : "#4a5568",
        boxShadow: active
          ? "0 2px 8px rgba(0,0,0,0.08)"
          : "0 1px 3px rgba(0,0,0,0.03)",
        transition: "all 0.2s ease",
      }}
    >
      {loading ? <Spin color={activeColor} size={18} /> : icon}
    </div>
    <span
      style={{
        fontSize: 11,
        color: isDark ? "#cbd5e1" : "#4a5568",
        fontWeight: 600,
        marginTop: 5,
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {label}
    </span>
  </button>
);

// ── Red Pill End Call Button ─────────────────────────────────────────────────
const EndCallButton = ({ onClick, loading = false, isDark = false }) => (
  <button
    onClick={onClick}
    disabled={loading}
    style={{
      width: "100%",
      padding: "13px 20px",
      borderRadius: 16,
      background: "#e03131",
      border: "none",
      color: "#ffffff",
      fontWeight: 700,
      fontSize: 15,
      cursor: loading ? "wait" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      boxShadow: "0 8px 20px rgba(224, 49, 49, 0.35)",
      transition: "all 0.15s ease",
      outline: "none",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = "#c92a2a")}
    onMouseLeave={(e) => (e.currentTarget.style.background = "#e03131")}
    onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
    onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
  >
    {loading ? (
      <>
        <Spin color="#fff" size={16} /> Ending...
      </>
    ) : (
      <>
        <div
          style={{
            transform: "rotate(135deg)",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <Phone size={17} color="#ffffff" strokeWidth={2.4} fill="#ffffff" />
        </div>
        End Call
      </>
    )}
  </button>
);

// ── Main Component ────────────────────────────────────────────────────────────
export default function FloatingDialer({
  initialLead = null,
  lead = null,
  autoOpen = false,
  autoCall = true,
  onClose,
  role = "agent",
}) {
  const { currentTheme } = useTheme();
  const isDark = ["Purple", "Black"].includes(currentTheme.name);
  const themeColor = currentTheme.color || "#449978";

  // ── Toast ─────────────────────────────────────────────────────────────────
  const useToast = () => {
    const [toasts, setToasts] = useState([]);
    const showToast = useCallback((message, type = "success", sub = "") => {
      const id = Date.now() + Math.random();
      setToasts((t) => [...t, { id, message, type, sub }]);
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
    }, []);

    const TOAST_STYLES = {
      success: {
        border: isDark ? "rgba(56,161,105,0.4)" : "#9AE6B4",
        left: "#38A169",
        title: isDark ? "#68D391" : "#276749",
        bg: isDark ? "#1a202c" : "#F0FFF4",
      },
      error: {
        border: isDark ? "rgba(229,62,62,0.4)" : "#FED7D7",
        left: "#E53E3E",
        title: isDark ? "#FC8181" : "#C53030",
        bg: isDark ? "#1a202c" : "#FFF5F5",
      },
      info: {
        border: isDark ? "rgba(49,130,206,0.4)" : "#BEE3F8",
        left: "#3182CE",
        title: isDark ? "#63B3ED" : "#2B6CB0",
        bg: isDark ? "#1a202c" : "#EBF8FF",
      },
    };

    const ToastContainer = (
      <div
        style={{
          position: "fixed",
          bottom: 100,
          right: 20,
          zIndex: 11000,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          pointerEvents: "none",
        }}
      >
        {toasts.map((toast) => {
          const s = TOAST_STYLES[toast.type] || TOAST_STYLES.info;
          return (
            <div
              key={toast.id}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                background: s.bg,
                borderRadius: 10,
                padding: "10px 14px",
                border: `1px solid ${s.border}`,
                borderLeft: `3px solid ${s.left}`,
                boxShadow: isDark
                  ? "0 4px 16px rgba(0,0,0,0.5)"
                  : "0 2px 16px rgba(0,0,0,0.1)",
                minWidth: 220,
                maxWidth: 300,
                animation: "toastIn 0.25s ease",
                fontFamily: "system-ui, sans-serif",
                pointerEvents: "auto",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: s.title }}>
                  {toast.message}
                </div>
                {toast.sub && (
                  <div
                    style={{
                      fontSize: 11,
                      color: isDark ? "#A0AEC0" : "#718096",
                      marginTop: 2,
                    }}
                  >
                    {toast.sub}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
    return { showToast, ToastContainer };
  };

  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [callState, setCallState] = useState("idle"); // idle | dialing | active | ended
  const [activeLead, setActiveLead] = useState(null);
  const [manualNumber, setManualNumber] = useState("");
  const [callDuration, setCallDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [speaker, setSpeaker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showActiveKeypad, setShowActiveKeypad] = useState(false);
  const [disposition, setDisposition] = useState("");
  const [remarks, setRemarks] = useState("");
  const [showDisposition, setShowDisposition] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [leadDispositions, setLeadDispositions] = useState([]);
  const [dialerCode, setDialerCode] = useState("CUBE_MAIN");
  const getUserDialerCampaign = () => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.dialerCampaign || u.orderCampaign || "Inbound";
    } catch {
      return "Inbound";
    }
  };
  const [cubeCampaignId, setCubeCampaignId] = useState(getUserDialerCampaign);
  const [callError, setCallError] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  // Stores { RecordId, ... } from CUBE success response
  const [cubeCallInfo, setCubeCallInfo] = useState(null);

  const timerRef = useRef(null);
  const BASE_URL = API_URL;
  const authToken = () => localStorage.getItem("authToken");
  const agentId = () => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.agentUser || u.cubeAgentId || u.employeeId || u.id || "1000";
    } catch {
      return "1000";
    }
  };
  const isCube = () => dialerCode === "CUBE_MAIN";

  // ── Central API caller ────────────────────────────────────────────────────
  const dialerPost = async (endpoint, body) => {
    const res = await fetch(`${BASE_URL}/dialer/${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken()}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false)
      throw new Error(data?.message || `Error ${res.status}`);
    return data;
  };

  // ── CUBE Click2Call via /api/dialer/call (GET internally) ─────────────────
  const cubeClick2Call = async (phone, lead = null) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const agentIdVal =
      user.agentUser || user.cubeAgentId || user.employeeId || user.id || "agent";
    const employeeDialerCampaign =
      user.dialerCampaign || user.orderCampaign || "Inbound";
    // Priority: lead campaign > employee dialer campaign > cubeCampaignId state
    const campaignId =
      lead?.campaign ||
      lead?.campaignName ||
      lead?.orderCampaign ||
      employeeDialerCampaign ||
      cubeCampaignId ||
      "Inbound";

    // ✅ Uses /api/dialer/call with extraParams — backend callCube() does GET to CUBE
    const res = await fetch(`${BASE_URL}/dialer/call`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken()}`,
      },
      body: JSON.stringify({
        dialerCode: "CUBE_MAIN",
        phoneNumber: phone,
        agentId: agentIdVal,
        extraParams: {
          Name: lead?.customerName || lead?.name || "Customer",
          CampaignID: campaignId,
          Priority: "1",
          Duplicate: "2",
          Remark: "Default",
          ClientID: String(lead?.id || lead?.leadId || ""),
        },
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false)
      throw new Error(data?.message || `CUBE Error ${res.status}`);
    return data;
  };

  // ── Sync campaign from logged-in employee on mount ───────────────────────
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      const empCampaign = u.dialerCampaign || u.orderCampaign;
      if (empCampaign) setCubeCampaignId(empCampaign);
    } catch (_) {}
  }, []);

  // ── Load dispositions ─────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const { leadDispositionApi } = await import("../services/api");
        const data = await leadDispositionApi.getAllDispositions();
        const active = (data || [])
          .filter((d) => d.status === true)
          .map((d) => ({
            key: d.dispositionName,
            label: d.dispositionName,
            color: null,
            id: d.dispositionId || d.id,
          }));
        setLeadDispositions(
          active.length > 0
            ? active
            : DISPOSITIONS.map((d, i) => ({ ...d, id: i + 4 })),
        );
      } catch {
        setLeadDispositions(DISPOSITIONS.map((d, i) => ({ ...d, id: i + 4 })));
      }
    };
    load();
  }, []);

  // ── MAKE CALL ─────────────────────────────────────────────────────────────
  const startCall = useCallback(
    async (lead = null) => {
      const target = lead || activeLead;
      if (target) setActiveLead(target);
      const phone =
        target?.contactNumber ||
        target?.phoneNumber ||
        target?.phone ||
        target?.mobileNumber ||
        manualNumber;
      if (!phone) return;

      setCallState("dialing");
      setActionLoading("call");
      setCallError("");
      setShowDisposition(false);
      setDisposition("");
      setRemarks("");
      setMuted(false);
      setHeld(false);
      setCallDuration(0);
      setCubeCallInfo(null);

      try {
        let data;
        if (isCube()) {
          data = await cubeClick2Call(phone, target);
          // ✅ Parse CUBE RecordId from rawResponse
          try {
            const raw =
              typeof data.rawResponse === "string"
                ? JSON.parse(data.rawResponse)
                : data.rawResponse;
            if (raw?.Response?.RecordId) setCubeCallInfo(raw.Response);
          } catch (_) {}
          showToast(
            "Call initiated via CUBE",
            "success",
            `Agent: ${agentId()} · ${cubeCampaignId}`,
          );
        } else {
          data = await dialerPost("call", {
            dialerCode,
            phoneNumber: phone,
            agentId: String(agentId()),
            extraParams: {
              campaign: target?.campaign || "",
              leadId: String(target?.id || ""),
            },
          });
          showToast(
            data?.message || "Call initiated",
            "success",
            `${data?.agentId || agentId()} · ${dialerCode}`,
          );
        }
        setCallState("active");
      } catch (err) {
        setCallError(err.message || "Call failed");
        setCallState("idle");
        showToast(
          err.message || "Call failed",
          "error",
          "Check agent login status",
        );
      } finally {
        setActionLoading("");
      }
    },
    [activeLead, manualNumber, dialerCode, cubeCampaignId, showToast],
  );

  // ── Global crm:dial event listener (Click to Call) ───────────────────────
  useEffect(() => {
    const handleDialEvent = (e) => {
      const currentLead = e?.detail?.lead;
      if (!currentLead) return;
      const phone =
        currentLead.contactNumber ||
        currentLead.phoneNumber ||
        currentLead.phone ||
        currentLead.mobileNumber ||
        "";
      if (!phone) {
        showToast("No phone number found for this lead", "error");
        return;
      }
      setActiveLead(currentLead);
      setManualNumber(phone);
      setIsOpen(true);
      setIsMinimized(false);
      setShowDisposition(false);
      setDisposition("");
      setRemarks("");
      setCallError("");
      startCall(currentLead);
    };

    window.addEventListener("crm:dial", handleDialEvent);
    return () => window.removeEventListener("crm:dial", handleDialEvent);
  }, [startCall, showToast]);

  // ── initialLead / lead prop (Mount only) ──────────────────────────────────
  useEffect(() => {
    const currentLead = lead || initialLead;
    if (currentLead) {
      const phone =
        currentLead.contactNumber ||
        currentLead.phoneNumber ||
        currentLead.phone ||
        currentLead.mobileNumber ||
        "";
      setActiveLead(currentLead);
      setManualNumber(phone);
      setIsOpen(true);
      setIsMinimized(false);
      setShowDisposition(false);
      setDisposition("");
      setRemarks("");
      setCallError("");
      if (phone && autoCall) startCall(currentLead);
    }
  }, []); // Run on initial mount if lead prop is passed directly

  // ── Auto-close on external Disposition Saved ──────────────────────────────
  useEffect(() => {
    const handleDispositionSaved = (e) => {
      const eventLeadId = e?.detail?.leadId;
      const currentLeadId = activeLead?.id || activeLead?.leadId;

      // Close if no specific leadId or if it matches active dialer lead
      if (!eventLeadId || !currentLeadId || String(eventLeadId) === String(currentLeadId)) {
        clearInterval(timerRef.current);

        // Always call hangup to disconnect telephony call
        dialerPost("hangup", {
          dialerCode,
          agentId: String(agentId()),
          extraParams: { hangup_type: "BOTH" },
        }).catch((err) => console.warn("Hangup on disposition saved:", err.message));

        setIsOpen(false);
        setShowDisposition(false);
        setCallState("idle");
        setActiveLead(null);
        setManualNumber("");
        setDisposition("");
        setRemarks("");
        setCallError("");
        setCubeCallInfo(null);
        setCallDuration(0);
        setMuted(false);
        setHeld(false);
        setActionLoading("");
        onClose?.();
        if (e?.detail?.dispositionName) {
          showToast(`Disposition saved: ${e.detail.dispositionName}`, "success");
        }
      }
    };

    window.addEventListener("crm:disposition_saved", handleDispositionSaved);
    window.addEventListener("crm:close_dialer", handleDispositionSaved);
    return () => {
      window.removeEventListener("crm:disposition_saved", handleDispositionSaved);
      window.removeEventListener("crm:close_dialer", handleDispositionSaved);
    };
  }, [activeLead, dialerCode, showToast, onClose]);

  // ── Timer ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (callState === "active") {
      timerRef.current = setInterval(() => setCallDuration((d) => d + 1), 1000);
    } else {
      clearInterval(timerRef.current);
      if (callState === "idle") setCallDuration(0);
    }
    return () => clearInterval(timerRef.current);
  }, [callState]);

  // ── HANGUP ────────────────────────────────────────────────────────────────
  const endCall = useCallback(async () => {
    if (actionLoading === "hangup") return;
    setActionLoading("hangup");
    const dur = callDuration;
    try {
      await dialerPost("hangup", {
        dialerCode,
        agentId: String(agentId()),
        extraParams: { hangup_type: "BOTH" },
      });
      showToast("Call ended", "info", `Duration: ${formatTime(dur)}`);
    } catch (err) {
      console.warn("Hangup:", err.message);
      showToast("Call ended", "info", `Duration: ${formatTime(dur)}`);
    } finally {
      setActionLoading("");
    }
    setCallState("ended");
    setShowDisposition(true);
    setCallHistory((h) =>
      [
        {
          name: activeLead?.customerName || activeLead?.name || manualNumber,
          number:
            activeLead?.contactNumber || activeLead?.phone || manualNumber,
          duration: dur,
          time: new Date(),
        },
        ...h,
      ].slice(0, 10),
    );
  }, [
    callDuration,
    activeLead,
    manualNumber,
    dialerCode,
    actionLoading,
    showToast,
  ]);

  // ── HOLD ──────────────────────────────────────────────────────────────────
  const toggleHold = useCallback(async () => {
    if (actionLoading) return;
    if (isCube()) {
      setHeld((h) => !h);
      showToast(!held ? "Call on hold" : "Call resumed", "info");
      return;
    }
    setActionLoading("hold");
    const newHeld = !held;
    try {
      await dialerPost("hold", {
        dialerCode,
        agentId: String(agentId()),
        extraParams: { hold: newHeld ? "true" : "false" },
      });
      setHeld(newHeld);
      showToast(newHeld ? "Call on hold" : "Call resumed", "info");
    } catch (err) {
      showToast(`Hold failed: ${err.message}`, "error");
    } finally {
      setActionLoading("");
    }
  }, [held, dialerCode, actionLoading, showToast]);

  // ── MUTE ──────────────────────────────────────────────────────────────────
  const toggleMute = useCallback(async () => {
    if (actionLoading) return;
    if (isCube()) {
      setMuted((m) => !m);
      showToast(!muted ? "Muted" : "Unmuted", "info");
      return;
    }
    setActionLoading("mute");
    const newMuted = !muted;
    try {
      await dialerPost("mute", {
        dialerCode,
        agentId: String(agentId()),
        extraParams: { mute: newMuted ? "true" : "false" },
      });
      setMuted(newMuted);
      showToast(newMuted ? "Muted" : "Unmuted", "info");
    } catch (err) {
      showToast(`Mute failed: ${err.message}`, "error");
    } finally {
      setActionLoading("");
    }
  }, [muted, dialerCode, actionLoading, showToast]);

  // ── SUBMIT DISPOSITION ────────────────────────────────────────────────────
  const submitDisposition = async (dispOverride) => {
    const finalDisp =
      typeof dispOverride === "string" ? dispOverride : disposition;
    if (!finalDisp) return;
    const targetLeadId = activeLead?.id || activeLead?.leadId;
    const wasOrderPlaced =
      finalDisp === "ORDER PLACED" || finalDisp === "Order Placed";
    const leadSnapshot = activeLead;
    const savedRemarks = remarks || "";

    // ✅ Timer band karo — call cut karo
    clearInterval(timerRef.current);

    // ✅ Hangup API silently call karo (CUBE and all dialers)
    if (callState === "active" || callState === "ended") {
      try {
        await dialerPost("hangup", {
          dialerCode,
          agentId: String(agentId()),
          extraParams: { hangup_type: "BOTH" },
        });
      } catch (e) {
        console.warn("Auto hangup on disposition submit:", e.message);
      }
    }

    // ✅ Saari state reset karo — dialer band karo
    setIsOpen(false);
    setShowDisposition(false);
    setCallState("idle");
    setActiveLead(null);
    setManualNumber("");
    setDisposition("");
    setRemarks("");
    setCallError("");
    setCubeCallInfo(null);
    setCallDuration(0);
    setMuted(false);
    setHeld(false);
    setActionLoading("");
    onClose?.();

    showToast(`Disposition: ${finalDisp}`, "success");

    (async () => {
      if (targetLeadId) {
        try {
          await leadsApi.updateLeadDisposition(
            targetLeadId,
            finalDisp,
            savedRemarks,
          );
        } catch (e) {
          console.warn(e);
        }
      }
      try {
        await fetch(`${BASE_URL}/disposition/submit`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken()}`,
          },
          body: JSON.stringify({
            dialerCode,
            agentId: String(agentId()),
            disposition: finalDisp,
            remarks: savedRemarks,
          }),
        });
      } catch (e) {
        console.warn(e);
      }
    })();

    if (wasOrderPlaced && role === "agent")
      navigate("/orders/add", { state: { leadData: leadSnapshot } });
  };

  const handleClose = () => {
    if (callState === "active") {
      dialerPost("hangup", {
        dialerCode,
        agentId: String(agentId()),
        extraParams: { hangup_type: "BOTH" },
      }).catch(() => {});
    }
    clearInterval(timerRef.current);
    setIsOpen(false);
    setCallState("idle");
    setActiveLead(null);
    setShowDisposition(false);
    setDisposition("");
    setRemarks("");
    setCallError("");
    setManualNumber("");
    setCubeCallInfo(null);
    setCallDuration(0);
    setMuted(false);
    setHeld(false);
    setActionLoading("");
    onClose?.();
  };

  // ── Shared styles ─────────────────────────────────────────────────────────
  const panel = {
    bg: isDark ? "#0f172a" : "#ffffff",
    border: isDark ? "rgba(255,255,255,0.1)" : "#E2E8F0",
    muted: isDark ? "#A0AEC0" : "#718096",
    text: isDark ? "#f1f5f9" : "#1a202c",
    subtle: isDark ? "#1e293b" : "#F7FAFC",
    input: isDark ? "#1e293b" : "#ffffff",
  };

  // ── Btn helper ────────────────────────────────────────────────────────────
  const iconBtn = (onClick, active, activeColor, icon, label, loading) => (
    <div style={{ textAlign: "center" }}>
      <button
        onClick={onClick}
        disabled={!!actionLoading}
        style={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          background: active ? `${activeColor}20` : panel.subtle,
          border: `1.5px solid ${active ? activeColor : panel.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: actionLoading ? "wait" : "pointer",
          margin: "0 auto 4px",
          opacity: loading ? 0.65 : 1,
          transition: "all 0.15s",
        }}
      >
        {loading ? <Spin color={activeColor} /> : icon}
      </button>
      <span style={{ fontSize: 11, color: panel.muted }}>{label}</span>
    </div>
  );

  // ── Floating button ───────────────────────────────────────────────────────
  const FloatingBtn = (
    <button
      onClick={() => {
        setIsOpen((o) => !o);
        setIsMinimized(false);
      }}
      style={{
        position: "fixed",
        right: 104,
        top: 13,
        zIndex: 10001,
        width: 48,
        height: 48,
        borderRadius: "50%",
        background: callState === "active" ? "#E53E3E" : themeColor,
        border: "2px solid rgba(255,255,255,0.4)",
        cursor: "pointer",
        boxShadow:
          callState === "active"
            ? "0 0 0 4px rgba(229,62,62,0.25), 0 4px 12px rgba(0,0,0,0.2)"
            : "0 2px 10px rgba(0,0,0,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 0.2s, box-shadow 0.2s",
      }}
    >
      {callState === "active" ? (
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: "#fff",
            animation: "dialPulse 1s infinite",
          }}
        />
      ) : (
        <Phone size={22} color="#fff" />
      )}
      {callState === "active" && (
        <span
          style={{
            position: "absolute",
            top: -4,
            right: -4,
            background: "#E53E3E",
            color: "#fff",
            fontSize: 9,
            fontWeight: 700,
            borderRadius: 10,
            padding: "1px 5px",
            fontFamily: "monospace",
            border: "1.5px solid #fff",
          }}
        >
          {formatTime(callDuration)}
        </span>
      )}
    </button>
  );

  if (!isOpen)
    return (
      <>
        <style>{`
        @keyframes dialPulse  { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.6;transform:scale(1.2)} }
        @keyframes toastIn    { from{opacity:0;transform:translateX(16px)} to{opacity:1;transform:translateX(0)} }
        @keyframes dialerSpin { to{transform:rotate(360deg)} }
      `}</style>
        {ToastContainer}
        {FloatingBtn}
      </>
    );

  const currentPhone =
    activeLead?.contactNumber ||
    activeLead?.phone ||
    activeLead?.phoneNumber ||
    activeLead?.mobileNumber ||
    manualNumber;
  const currentName = activeLead?.customerName || activeLead?.name || "Unknown";

  return (
    <>
      <style>{`
        @keyframes dialPulse  { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.6;transform:scale(1.2)} }
        @keyframes ringPulse  { 0%,100%{box-shadow:0 0 0 0 rgba(68,153,120,0.5)} 70%{box-shadow:0 0 0 12px rgba(68,153,120,0)} }
        @keyframes dialerSpin { to{transform:rotate(360deg)} }
        @keyframes toastIn    { from{opacity:0;transform:translateX(16px)} to{opacity:1;transform:translateX(0)} }
        @keyframes fadeSlideIn{ from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .dialer-disp-btn:hover { filter: brightness(1.05); }
      `}</style>

      {ToastContainer}
      {FloatingBtn}

      {/* ── Panel ─────────────────────────────────────────────────────────── */}
      <div
        style={{
          position: "fixed",
          right: 24,
          top: 74,
          zIndex: 10000,
          width: 340,
          maxWidth: "94vw",
          borderRadius: 24,
          background: isDark ? "#0f172a" : "#ffffff",
          border: isDark
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(0,0,0,0.06)",
          boxShadow: isDark
            ? "0 20px 48px rgba(0,0,0,0.6)"
            : "0 18px 45px rgba(0,0,0,0.14), 0 2px 10px rgba(0,0,0,0.04)",
          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          overflow: "hidden",
          animation: "fadeSlideIn 0.2s ease",
        }}
      >
        {/* ── Top Header with Leaves, Aarogya India Logo & Minimize Button ── */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingTop: 16,
            paddingBottom: 8,
            paddingLeft: 16,
            paddingRight: 16,
          }}
        >
          {/* Botanical leaves in top left */}
          <div
            style={{
              position: "absolute",
              left: 12,
              top: 10,
              pointerEvents: "none",
            }}
          >
            <BotanicalLeaves />
          </div>

          {/* Authentic Aarogya India Hindi Logo in Center */}
          <img
            src={aarogyaLogo}
            alt="आरोग्य इंडिया"
            style={{
              height: 38,
              maxHeight: 38,
              objectFit: "contain",
            }}
          />

          {/* Action buttons (Minimize & Close) in top right */}
          <div
            style={{
              position: "absolute",
              right: 14,
              top: 14,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <button
              onClick={() => setIsMinimized((m) => !m)}
              title="Minimize"
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: isDark ? "rgba(255,255,255,0.12)" : "#e2edd9",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <Minus
                size={14}
                color={isDark ? "#fff" : "#3d6d45"}
                strokeWidth={2.5}
              />
            </button>
            <button
              onClick={handleClose}
              title="Close"
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: isDark ? "rgba(255,255,255,0.08)" : "#f1f5f0",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <X
                size={13}
                color={isDark ? "#cbd5e1" : "#64748b"}
                strokeWidth={2.2}
              />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <div style={{ padding: "8px 16px 20px" }}>
            {/* ══════════════ IDLE STATE (MANUAL DIALPAD) ══════════════ */}
            {callState === "idle" && !showDisposition && (
              <div>
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    marginBottom: 10,
                    alignItems: "center",
                  }}
                >
                  <select
                    value={dialerCode}
                    onChange={(e) => setDialerCode(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      borderRadius: 8,
                      border: `1px solid ${panel.border}`,
                      fontSize: 11,
                      color: panel.text,
                      background: panel.subtle,
                      outline: "none",
                      cursor: "pointer",
                    }}
                  >
                    <option value="CUBE_MAIN">CUBE MAIN</option>
                  </select>
                </div>

                <input
                  type="tel"
                  value={manualNumber}
                  onChange={(e) => setManualNumber(e.target.value)}
                  placeholder="Enter phone number..."
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: 12,
                    border: `1.5px solid ${panel.border}`,
                    fontSize: 15,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textAlign: "center",
                    outline: "none",
                    marginBottom: 10,
                    boxSizing: "border-box",
                    color: panel.text,
                    background: panel.input,
                  }}
                />

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 6,
                    marginBottom: 12,
                  }}
                >
                  {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map(
                    (d) => (
                      <button
                        key={d}
                        onClick={() => setManualNumber((n) => n + d)}
                        style={{
                          padding: "8px 0",
                          borderRadius: 8,
                          border: `1px solid ${panel.border}`,
                          background: panel.subtle,
                          color: panel.text,
                          fontSize: 15,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {d}
                      </button>
                    ),
                  )}
                </div>

                <button
                  onClick={() => startCall()}
                  disabled={!manualNumber || actionLoading === "call"}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: 14,
                    background: "#2f855a",
                    border: "none",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: manualNumber ? "pointer" : "not-allowed",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    opacity: manualNumber ? 1 : 0.6,
                    boxShadow: "0 4px 14px rgba(47, 133, 90, 0.35)",
                  }}
                >
                  <Phone size={16} /> Call
                </button>
              </div>
            )}

            {/* ══════════════ IMAGE 1: DIALING STATE (JAB CALL JAYE / RINGING) ══════════════ */}
            {callState === "dialing" && !showDisposition && (
              <div style={{ textAlign: "center", padding: "6px 4px 0" }}>
                <ConcentricAvatar isDialing={true} isDark={isDark} />

                <div
                  style={{
                    fontSize: 13,
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontWeight: 500,
                    marginBottom: 2,
                  }}
                >
                  Calling...
                </div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: isDark ? "#f8fafc" : "#1a202c",
                    letterSpacing: "-0.2px",
                  }}
                >
                  {currentName}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontWeight: 500,
                    letterSpacing: "0.5px",
                    marginTop: 3,
                  }}
                >
                  {maskPhone(currentPhone)}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontWeight: 500,
                    marginTop: 6,
                    marginBottom: 24,
                  }}
                >
                  हम आपसे बात करना चाहते हैं
                </div>

                {callError && (
                  <div
                    style={{
                      background: isDark ? "rgba(229,62,62,0.15)" : "#FFF5F5",
                      border: "1px solid #FED7D7",
                      borderRadius: 8,
                      padding: "6px 12px",
                      marginBottom: 14,
                      fontSize: 12,
                      color: "#e53e3e",
                    }}
                  >
                    {callError}
                  </div>
                )}

                <EndCallButton
                  onClick={endCall}
                  loading={actionLoading === "hangup"}
                  isDark={isDark}
                />
              </div>
            )}

            {/* ══════════════ IMAGE 2: ACTIVE CALL STATE (JAB CALL UTH JAYE / CONNECTED) ══════════════ */}
            {callState === "active" && !showDisposition && (
              <div style={{ textAlign: "center", padding: "6px 4px 0" }}>
                <ConcentricAvatar isDialing={false} isDark={isDark} />

                <div
                  style={{
                    fontSize: 12,
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontWeight: 500,
                    marginBottom: 1,
                  }}
                >
                  Calling...
                </div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: isDark ? "#f8fafc" : "#1a202c",
                    letterSpacing: "-0.2px",
                  }}
                >
                  {currentName}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontWeight: 500,
                    letterSpacing: "0.5px",
                    marginTop: 2,
                  }}
                >
                  {maskPhone(currentPhone)}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontWeight: 500,
                    marginTop: 4,
                  }}
                >
                  हम आपसे बात करना चाहते हैं
                </div>

                {/* Call duration timer */}
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: isDark ? "#f8fafc" : "#1a202c",
                    marginTop: 6,
                    marginBottom: 18,
                    fontFamily: "system-ui, monospace",
                    letterSpacing: "0.5px",
                  }}
                >
                  {formatTime(callDuration)}
                </div>

                {/* 6 Circular Action Buttons (2 rows x 3 columns) */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "16px 8px",
                    marginBottom: 22,
                    padding: "0 4px",
                  }}
                >
                  {/* 1. Mute */}
                  <CircleActionButton
                    icon={
                      muted ? (
                        <MicOff size={20} color="#e53e3e" />
                      ) : (
                        <Mic size={20} />
                      )
                    }
                    label="Mute"
                    onClick={toggleMute}
                    active={muted}
                    activeBg="#fee2e2"
                    activeColor="#dc2626"
                    loading={actionLoading === "mute"}
                    isDark={isDark}
                  />

                  {/* 2. Keypad */}
                  <CircleActionButton
                    icon={<Grid size={20} />}
                    label="Keypad"
                    onClick={() => setShowActiveKeypad((k) => !k)}
                    active={showActiveKeypad}
                    isDark={isDark}
                  />

                  {/* 3. Speaker */}
                  <CircleActionButton
                    icon={
                      speaker ? (
                        <Volume2 size={20} color="#2b6cb0" />
                      ) : (
                        <Volume2 size={20} />
                      )
                    }
                    label="Speaker"
                    onClick={() => {
                      setSpeaker((s) => !s);
                      showToast(
                        !speaker ? "Speaker On" : "Speaker Off",
                        "info",
                      );
                    }}
                    active={speaker}
                    activeBg="#e0f2fe"
                    activeColor="#0284c7"
                    isDark={isDark}
                  />

                  {/* 4. Add Call */}
                  <CircleActionButton
                    icon={<Plus size={20} />}
                    label="Add Call"
                    onClick={() =>
                      showToast("Conference call not available", "info")
                    }
                    isDark={isDark}
                  />

                  {/* 5. Hold */}
                  <CircleActionButton
                    icon={
                      held ? (
                        <Play size={20} color="#d97706" />
                      ) : (
                        <Pause size={20} />
                      )
                    }
                    label="Hold"
                    onClick={toggleHold}
                    active={held}
                    activeBg="#fef3c7"
                    activeColor="#d97706"
                    loading={actionLoading === "hold"}
                    isDark={isDark}
                  />

                  {/* 6. Record */}
                  <CircleActionButton
                    icon={
                      <Disc
                        size={20}
                        color={isRecording ? "#e53e3e" : undefined}
                      />
                    }
                    label="Record"
                    onClick={() => {
                      setIsRecording((r) => !r);
                      showToast(
                        !isRecording
                          ? "Call Recording Started"
                          : "Recording Stopped",
                        "info",
                      );
                    }}
                    active={isRecording}
                    activeBg="#fee2e2"
                    activeColor="#dc2626"
                    isDark={isDark}
                  />
                </div>

                {/* Keypad Overlay if active */}
                {showActiveKeypad && (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: "8px",
                      background: isDark
                        ? "rgba(255,255,255,0.05)"
                        : "#f8fafc",
                      borderRadius: 12,
                      border: `1px solid ${panel.border}`,
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 6,
                      }}
                    >
                      {[
                        "1",
                        "2",
                        "3",
                        "4",
                        "5",
                        "6",
                        "7",
                        "8",
                        "9",
                        "*",
                        "0",
                        "#",
                      ].map((k) => (
                        <button
                          key={k}
                          onClick={() => showToast(`Key: ${k}`, "info")}
                          style={{
                            padding: "8px 0",
                            borderRadius: 8,
                            border: `1px solid ${panel.border}`,
                            background: isDark
                              ? "rgba(255,255,255,0.08)"
                              : "#fff",
                            color: panel.text,
                            fontSize: 15,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          {k}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <EndCallButton
                  onClick={endCall}
                  loading={actionLoading === "hangup"}
                  isDark={isDark}
                />
              </div>
            )}

            {/* ══════════════ DISPOSITION STATE ══════════════ */}
            {showDisposition && (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 14,
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: isDark ? "rgba(229,62,62,0.1)" : "#FFF5F5",
                    border: `1px solid ${isDark ? "rgba(229,62,62,0.25)" : "#FED7D7"}`,
                  }}
                >
                  <PhoneOff size={14} color={isDark ? "#FC8181" : "#C53030"} />
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: isDark ? "#FC8181" : "#C53030",
                      }}
                    >
                      Call Ended
                    </div>
                    <div style={{ fontSize: 11, color: panel.muted }}>
                      Duration: {formatTime(callDuration)}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    fontSize: 11,
                    color: panel.muted,
                    fontWeight: 600,
                    marginBottom: 8,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Select Disposition
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                    maxHeight: 260,
                    overflowY: "auto",
                    paddingRight: 4,
                    marginBottom: 12,
                  }}
                >
                  {leadDispositions
                    .filter((d) =>
                      role === "verification"
                        ? [
                            "VERIFIED",
                            "REJECTED",
                            "CALL BACK",
                            "NOT CONNECTED",
                            "SWITCH OFF",
                          ].includes(d.key)
                        : !["VERIFIED", "REJECTED"].includes(d.key),
                    )
                    .map((d) => (
                      <button
                        key={d.key}
                        className="dialer-disp-btn"
                        onClick={() => setDisposition(d.key)}
                        style={{
                          padding: "8px 8px",
                          borderRadius: 9,
                          fontSize: 12,
                          cursor: "pointer",
                          textAlign: "left",
                          fontWeight: disposition === d.key ? 700 : 400,
                          border:
                            disposition === d.key
                              ? `2px solid ${d.color || themeColor}`
                              : `1px solid ${panel.border}`,
                          background:
                            disposition === d.key
                              ? (d.color || themeColor) + "18"
                              : panel.subtle,
                          color:
                            disposition === d.key
                              ? d.color || themeColor
                              : panel.text,
                          transition: "all 0.12s",
                        }}
                      >
                        {d.label}
                      </button>
                    ))}
                </div>

                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Remarks (optional)"
                  rows={2}
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: 9,
                    border: `1px solid ${panel.border}`,
                    fontSize: 12,
                    resize: "none",
                    outline: "none",
                    marginBottom: 12,
                    boxSizing: "border-box",
                    color: panel.text,
                    background: panel.input,
                    fontFamily: "system-ui, sans-serif",
                  }}
                />

                <div style={{ display: "flex", gap: 8 }}>
                  {disposition === "ORDER PLACED" && (
                    <button
                      onClick={() => {
                        submitDisposition();
                        if (activeLead) {
                          navigate(`/orders/new?leadId=${activeLead.id}`);
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: "11px",
                        borderRadius: 10,
                        background: "#276749",
                        border: "none",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                      }}
                    >
                      <ShoppingCart size={14} /> Place Order
                    </button>
                  )}
                  {disposition === "VERIFIED" && role === "verification" && (
                    <button
                      onClick={submitDisposition}
                      style={{
                        flex: 1,
                        padding: "11px",
                        borderRadius: 10,
                        background: "#276749",
                        border: "none",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                      }}
                    >
                      <Shield size={14} /> Confirm Verified
                    </button>
                  )}
                  {disposition !== "ORDER PLACED" &&
                    disposition !== "VERIFIED" && (
                      <button
                        onClick={submitDisposition}
                        disabled={!disposition}
                        style={{
                          flex: 1,
                          padding: "11px",
                          borderRadius: 10,
                          background: disposition
                            ? `linear-gradient(135deg, ${themeColor}, ${themeColor}cc)`
                            : isDark
                              ? "rgba(255,255,255,0.08)"
                              : "#E2E8F0",
                          border: "none",
                          color: disposition ? "#fff" : panel.muted,
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: disposition ? "pointer" : "not-allowed",
                          boxShadow: disposition
                            ? `0 4px 10px ${themeColor}35`
                            : "none",
                        }}
                      >
                        Submit & Close
                      </button>
                    )}
                  <button
                    onClick={handleClose}
                    style={{
                      padding: "11px 16px",
                      borderRadius: 10,
                      border: `1px solid ${panel.border}`,
                      background: "transparent",
                      fontSize: 12,
                      color: panel.muted,
                      cursor: "pointer",
                    }}
                  >
                    Skip
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
